import { UseFieldArrayUpdate, useFormContext } from 'react-hook-form';
import { ClientInputFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import React from 'react';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import CacPhoneInput from '@/components/form/CacPhoneInput.tsx';
import PostalCodeInput from '@/components/form/PostalCodeInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import { useInitParticipantValues } from '@/features/public/booking/hooks/useInitParticipantValues.ts';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { applyFillerTypeToFirstParticipant } from '@/features/public/booking/util/bookingFormDefaults.ts';
import { FillerType } from '@/features/public/booking/types/FillerType.ts';
import { FILLER_TYPE_OPTIONS } from '@/features/public/booking/types/FillerType.utils.ts';
import { useParticipantPricingErrors } from '@/features/public/booking/context/BookingQuoteContext.tsx';
import { placePricingErrors } from '@/features/public/booking/util/pricingErrorPlacement.ts';

interface ClientBookFormProps {
  updateParticipant: UseFieldArrayUpdate<
    ClientInputFormWithCustomFields,
    'participants'
  >;
  participantForm: CustomFieldDefinitionDto[];
}

const ClientBookForm = ({
  updateParticipant,
  participantForm,
}: ClientBookFormProps) => {
  const {
    control,
    register,
    formState: { errors, isSubmitted },
    setValue,
    getValues,
    watch,
  } = useFormContext<ClientInputFormWithCustomFields>();

  const { getParticipantInitValues } =
    useInitParticipantValues(participantForm);

  const quoteErrors = useParticipantPricingErrors(0);

  // Wiersz uczestnika nr 0 nie rusza się z miejsca — przełącznik decyduje tylko
  // o tym, czy opisuje rezerwującego, czy kogoś innego.
  const onFillerTypeChange = (value: string) => {
    const firstParticipant =
      getValues('participants.0') ?? getParticipantInitValues();

    updateParticipant(
      0,
      applyFillerTypeToFirstParticipant(firstParticipant, value as FillerType, {
        firstName: getValues('firstName'),
        lastName: getValues('lastName'),
        birthDate: getValues('birthDate'),
      }),
    );
  };

  // Po nieudanej wysyłce pole uczestnika ma się odczerwienić razem z polem
  // rezerwującego; wcześniej walidacja tylko by przeszkadzała.
  const mirrorOptions = { shouldValidate: isSubmitted, shouldDirty: true };

  const onFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (getValues('fillerType') === FillerType.PARTICIPANT) {
      setValue('participants.0.firstName', e.target.value, mirrorOptions);
    }
  };

  const onLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (getValues('fillerType') === FillerType.PARTICIPANT) {
      setValue('participants.0.lastName', e.target.value, mirrorOptions);
    }
  };

  const onBirthDateChange = (date: Date | undefined) => {
    if (getValues('fillerType') === FillerType.PARTICIPANT) {
      setValue('participants.0.birthDate', date, mirrorOptions);
    }
  };

  // Rezerwujący będący uczestnikiem wycenia się po swoim wieku, więc „brak ceny
  // dla tego wieku" należy do tej daty urodzenia, a nie do odbicia w karcie.
  const birthDateQuoteError =
    watch('fillerType') === FillerType.PARTICIPANT
      ? placePricingErrors(quoteErrors, {
          options: [],
          hasTransportSelector: false,
        }).birthDate[0]
      : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Osoba rezerwująca</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <ToggleInput
          fieldPath="fillerType"
          label="Formularz wypełnia"
          options={FILLER_TYPE_OPTIONS}
          control={control}
          onChange={onFillerTypeChange}
          required
        />
        <TwoInputsRow>
          <TextInput
            label="Imię"
            register={register('firstName')}
            placeholder="Wpisz imię..."
            required
            error={errors.firstName?.message}
            onChange={onFirstNameChange}
            autoComplete="given-name"
            enterKeyHint="next"
          />
          <TextInput
            label="Nazwisko"
            register={register('lastName')}
            placeholder="Wpisz nazwisko..."
            required
            error={errors.lastName?.message}
            onChange={onLastNameChange}
            autoComplete="family-name"
            enterKeyHint="next"
          />
        </TwoInputsRow>
        <DateInput
          label="Data urodzenia"
          dateFieldName="birthDate"
          control={control}
          required
          error={errors.birthDate?.message ?? birthDateQuoteError}
          onChange={onBirthDateChange}
        />
        <TextInput
          label="Adres e-mail"
          register={register('email')}
          placeholder="Wpisz adres e-mail..."
          required
          error={errors.email?.message}
          type="email"
          autoComplete="email"
          enterKeyHint="next"
        />
        <CacPhoneInput
          label="Numer telefonu"
          fieldPath="phoneNumber"
          error={errors.phoneNumber?.message}
        />
        <TextInput
          label="Adres"
          register={register('address')}
          placeholder="Wpisz nazwę ulicy, numer budynku i/lub mieszkania..."
          required
          error={errors.address?.message}
          autoComplete="street-address"
          enterKeyHint="next"
        />
        <TwoInputsRow ratio="1/3">
          <PostalCodeInput
            label="Kod pocztowy"
            register={register('postalCode')}
            required
            error={errors.postalCode?.message}
            autoComplete="postal-code"
            enterKeyHint="next"
          />
          <TextInput
            label="Miasto"
            register={register('city')}
            placeholder="Wpisz miasto..."
            required
            error={errors.city?.message}
            autoComplete="address-level2"
            enterKeyHint="next"
          />
        </TwoInputsRow>
      </CardContent>
    </Card>
  );
};

export default ClientBookForm;
